// IframeZoom.tsx
import React, {useRef, useState, useEffect} from 'react';
import {StyleSheet, View, TouchableOpacity} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import ENV from '../../../../../environment';
import {FloatingWindowConfirmModal} from './FloatingWindowConfirmModal';
import {CoachScreenModal} from './CoachScreenModal';
import {
  getBrowserInfo,
  getPipStatusMessage,
} from '../../utils/BrowserDetection/browserDetection';
import useClassroomStore from '../../store/timerStore';

const {ZOOM_IFRAME_BASE_URL} = ENV;

interface ZoomMeetingInfo {
  meetingId: string;
  tk: string;
  pwd: string;
  signature: string;
  userName: string;
  userEmail: string;
}

interface IframeZoomProps {
  zoomMeetingInfo: ZoomMeetingInfo;
  meetingRole: string;
  isZoomMeeting: number;
}

type WindowMode = 'normal' | 'floating' | 'preparing-pip' | null;

const IframeZoom: React.FC<IframeZoomProps> = ({
  zoomMeetingInfo,
  meetingRole,
  isZoomMeeting,
}) => {
  // const ZOOM_IFRAME_BASE_URL =
  //   window.location.origin || 'https://learn.circlechess.com';

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const originalContainerRef = useRef<HTMLDivElement | null>(null);
  const pipWindowRef = useRef<Window | null>(null);
  const setBeginTimer = useClassroomStore(state => state.setBeginTimer);
  const [showModeModal, setShowModeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [windowMode, setWindowMode] = useState<WindowMode>(null);
  const [browserInfo, setBrowserInfo] = useState({
    name: '',
    version: 0,
    isChrome: false,
    isEdge: false,
    isPipSupported: false,
    isPipCompatible: false,
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const iframeSrc =
    `${ZOOM_IFRAME_BASE_URL}/?zoomEmbed=true` +
    `&meetingId=${encodeURIComponent(zoomMeetingInfo.meetingId)}` +
    `&tk=${encodeURIComponent(zoomMeetingInfo.tk)}` +
    `&pwd=${encodeURIComponent(zoomMeetingInfo.pwd)}` +
    `&signature=${encodeURIComponent(zoomMeetingInfo.signature)}` +
    `&userName=${encodeURIComponent(zoomMeetingInfo.userName)}` +
    `&userEmail=${encodeURIComponent(zoomMeetingInfo.userEmail)}`;

  useEffect(() => {
    // Get browser info on mount
    const info = getBrowserInfo();
    setBrowserInfo(info);

    // Show mode selection modal for non-students
    if (meetingRole !== 'student' && isZoomMeeting === 1) {
      setShowModeModal(true);
    } else {
      // Students go directly to normal mode
      setWindowMode('normal');
    }

    // Cleanup on unmount
    return () => {
      closeDocumentPiP();
    };
  }, [meetingRole, isZoomMeeting]);

  const handleNormalWindow = () => {
    setShowModeModal(false);
    setWindowMode('normal');
    setBeginTimer(true);
  };

  const handleFloatingWindowFromModal = () => {
    setShowModeModal(false);
    // Set preparing state to load iframe first
    setWindowMode('preparing-pip');
    setBeginTimer(true);
  };

  const handleFloatingWindowFromButton = () => {
    // Check if PiP is supported and compatible before showing confirm modal
    if (!browserInfo.isPipSupported || !browserInfo.isPipCompatible) {
      alert(getPipStatusMessage(browserInfo));
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmFloatingWindow = () => {
    setShowConfirmModal(false);
    openDocumentPiP();
  };

  // When iframe loads
  const handleIframeLoad = () => {
    // If in preparing-pip mode, open PiP after a short delay
    if (windowMode === 'preparing-pip') {
      setTimeout(() => {
        openDocumentPiP();
      }, 500);
    }
  };

  const openDocumentPiP = async () => {
    try {
      if (!browserInfo.isPipSupported || !browserInfo.isPipCompatible) {
        alert(getPipStatusMessage(browserInfo));
        setWindowMode('normal');
        return;
      }

      // Ensure iframe exists
      if (!iframeRef.current) {
        console.error('Iframe not ready');
        setWindowMode('normal');
        return;
      }

      // Close existing PiP window if any
      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        pipWindowRef.current.close();
      }

      // Get screen dimensions for sizing
      const screenWidth = window.screen.availWidth;
      const screenHeight = window.screen.availHeight;

      // Calculate sizes
      const normalWidth = Math.floor(screenWidth * 0.5) - 150;
      const normalHeight = Math.floor(screenHeight * 0.5) + 100;
      const minWidth = 300;
      const minHeight = 85;

      // Open PiP window with 50% default size
      const pipWindow = await (
        window as any
      ).documentPictureInPicture.requestWindow({
        width: normalWidth + 100, //800, //normalWidth,
        height: normalHeight + 100, //600, //normalHeight,
      });

      pipWindowRef.current = pipWindow;

      // Add styles to PiP window
      const styleElement = pipWindow.document.createElement('style');
      styleElement.textContent = `
        body {
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
          background: #1a1a1a;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 100vh;
        }
        #header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 5px 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
          gap: 16px;
        }
        #warningText {
          font-size: 13px;
          font-weight: 600;
          margin: 0;
          flex: 1;
          min-width: 0;
        }
        #controls {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }
        .control-btn {
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 5px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
          white-space: nowrap;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
          min-height: 36px;
        }
        .control-btn:hover {
          background: rgba(255,255,255,0.3);
          transform: translateY(-1px);
        }
        .control-btn:active {
          background: rgba(255,255,255,0.4);
          transform: translateY(0);
        }
        #pipIframeContainer {
          flex: 1;
          width: 100%;
          position: relative;
          overflow: hidden;
        }
        #mainIframe {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
        }
      `;
      pipWindow.document.head.appendChild(styleElement);

      const header = pipWindow.document.createElement('div');
      header.id = 'header';
      header.innerHTML = `
        <p id="warningText">⚠️ Closing this window will close the meeting</p>
        <div id="controls">
          <button class="control-btn" id="resizeBtn" title="Resize to 50%">⛶</button>
          <button class="control-btn" id="minimizeBtn" title="Minimize">🗕</button>
        </div>
      `;
      pipWindow.document.body.appendChild(header);

      // Create container for iframe
      const pipIframeContainer = pipWindow.document.createElement('div');
      pipIframeContainer.id = 'pipIframeContainer';
      pipWindow.document.body.appendChild(pipIframeContainer);

      // Move the existing iframe element (maintains connection)
      if (iframeRef.current) {
        pipIframeContainer.appendChild(iframeRef.current);
      }

      // Control button handlers
      const resizeBtn = pipWindow.document.getElementById('resizeBtn');
      const minimizeBtn = pipWindow.document.getElementById('minimizeBtn');

      // Resize handler - Resize to 50%
      resizeBtn?.addEventListener('click', () => {
        try {
          pipWindow.resizeTo(normalWidth, normalHeight);
        } catch (error) {
          console.error('Resize error:', error);
          alert('Unable to resize window. Please manually resize the window.');
        }
      });

      // Minimize handler - smallest possible size
      minimizeBtn?.addEventListener('click', () => {
        try {
          pipWindow.resizeTo(minWidth, minHeight);
        } catch (error) {
          console.error('Resize error:', error);
        }
      });

      // Handle PiP window close event
      pipWindow.addEventListener('pagehide', () => {
        // Move iframe back to original container
        if (originalContainerRef.current && iframeRef.current) {
          originalContainerRef.current.appendChild(iframeRef.current);
        }
        pipWindowRef.current = null;
        setWindowMode('normal');
      });

      setWindowMode('floating');
      console.log('Document PiP window opened successfully');
    } catch (error) {
      console.error('Error opening PiP:', error);
      alert('Error opening floating window: ' + (error as Error).message);
      setWindowMode('normal');
    }
  };

  const closeDocumentPiP = () => {
    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      // Move iframe back to original container before closing
      if (originalContainerRef.current && iframeRef.current) {
        originalContainerRef.current.appendChild(iframeRef.current);
      }
      pipWindowRef.current.close();
      pipWindowRef.current = null;
      setWindowMode('normal');
    }
  };

  // Check if floating window button should be enabled
  const isFloatingButtonEnabled =
    browserInfo.isPipSupported && browserInfo.isPipCompatible;
  const pipStatusMessage = getPipStatusMessage(browserInfo);

  // Only render iframe if mode has been chosen (or is being prepared)
  const shouldRenderIframe = windowMode !== null;

  return (
    <>
      {/* Mode Selection Modal for non-students */}
      <CoachScreenModal
        visible={showModeModal}
        onClose={() => {
          setShowModeModal(false);
          // If user closes without choosing, default to normal
          if (windowMode === null) {
            setWindowMode('normal');
          }
        }}
        onNormalWindow={handleNormalWindow}
        onFloatingWindow={handleFloatingWindowFromModal}
        isPipSupported={isFloatingButtonEnabled}
        browserName={browserInfo.name}
        browserVersion={browserInfo.version}
      />

      {/* Confirmation Modal for switching to floating */}
      <FloatingWindowConfirmModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmFloatingWindow}
      />

      {/* Main Container - Only show if iframe should be rendered */}
      {shouldRenderIframe && (
        <View
          style={[
            styles.container,
            (windowMode === 'floating' || windowMode === 'preparing-pip') &&
              styles.hidden,
          ]}>
          {/* Floating Window Button - Only show for non-students in normal mode */}
          {meetingRole !== 'student' &&
            isZoomMeeting === 1 &&
            windowMode === 'normal' && (
              <View style={styles.floatingButtonContainer}>
                <TouchableOpacity
                  style={[
                    styles.floatingButton,
                    !isFloatingButtonEnabled && styles.floatingButtonDisabled,
                  ]}
                  onPress={handleFloatingWindowFromButton}
                  onMouseEnter={() =>
                    !isFloatingButtonEnabled && setShowTooltip(true)
                  }
                  onMouseLeave={() => setShowTooltip(false)}
                  disabled={!isFloatingButtonEnabled}>
                  <Icon
                    name="picture-in-picture-alt"
                    size={20}
                    color={
                      isFloatingButtonEnabled ? CCColors.White : CCColors.Black
                    }
                  />
                  <CCText
                    fontSize={FontSizes[12]}
                    color={
                      isFloatingButtonEnabled ? CCColors.White : CCColors.Black
                    }
                    fontWeight="medium"
                    style={{marginLeft: 6}}>
                    Floating Window
                  </CCText>
                </TouchableOpacity>

                {/* Tooltip for disabled button */}
                {showTooltip &&
                  !isFloatingButtonEnabled &&
                  pipStatusMessage && (
                    <View style={styles.tooltip}>
                      <CCText fontSize={FontSizes[11]} color={CCColors.White}>
                        {pipStatusMessage}
                      </CCText>
                    </View>
                  )}
              </View>
            )}

          {/* Iframe Container */}
          <div
            ref={originalContainerRef}
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
            }}>
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              style={{
                border: 0,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              allow="camera; microphone; display-capture; autoplay; fullscreen"
              title="Zoom Meeting"
              onLoad={handleIframeLoad}
            />
          </div>
        </View>
      )}
    </>
  );
};

export default IframeZoom;

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    paddingHorizontal: 'auto',
    width: '100%',
    height: '100%',
    aspectRatio: 4 / 3,
  },
  hidden: {
    display: 'none',
  },
  floatingButtonContainer: {
    position: 'absolute',
    top: 8,
    left: 10,
    zIndex: 1000,
  },
  floatingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.Primary.Dark,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  floatingButtonDisabled: {
    backgroundColor: CCColors.Grey.Grey,
    opacity: 0.6,
  },
  tooltip: {
    position: 'absolute',
    top: 45,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    padding: 8,
    borderRadius: 4,
    maxWidth: 250,
    zIndex: 1001,
  },
});
